import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Card,
  CircularProgress,
  TextField,
} from '@mui/material';
import Box from '@mui/material/Box';
import {
  DataGridPro,
  GridColDef,
  GridOverlay,
  GridSortModel,
  GridFilterModel,
} from '@mui/x-data-grid-pro';

import BaseToolbar from '../Datagrid/BaseToolbar';
import LinkIcon from '@mui/icons-material/Link';
import { normalizeFilterModel, sameModel, stringContainsOnly } from '../../utils/adminDashboardUtils';
import { Workflow } from '../../interfaces/workflow.interface';
import { fetchWorkflows } from '../../services/workflow';
import { fetchSurvey } from '../../services/surveys';
import { fetchAssessmentSurvey } from '../../services/assessments';
import { AssessmentSurvey } from '../../interfaces/assessment.interface';
import { Survey } from '../../interfaces/survey.interface';
import { Company } from '../../interfaces/users.interface';
import WorkflowStatus, { WorkflowStatusDisplay } from '../Directory/WorkflowStatus';
import { getCompanies } from '../../services/company';

type WorkflowDisplayStatusVerificationRow = {
  workflow: Workflow;
  bpSurvey: Survey | null;
  assessmentSurvey: AssessmentSurvey | null;
  company: Company | null;
  id: number;
};

const getSiteOrAppDisplayValue = (workflow: Workflow): string => {
  const site = workflow.site;
  const application = workflow.application;

  if (typeof site === 'number' || typeof site === 'string') {
    return String(site);
  }

  if (typeof application === 'number' || typeof application === 'string') {
    return String(application);
  }

  if (application && typeof application === 'object' && 'id' in application) {
    return String(application.id);
  }

  return '';
};

export default function WorkflowDisplayStatusVerification() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [rowCount, setRowCount] = useState(0);
  const [rows, setRows] = useState<WorkflowDisplayStatusVerificationRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModelDraft, setFilterModelDraft] = useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = useState<GridFilterModel>({ items: [] });

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const fetchCompaniesForDropdown = useCallback(async () => {
    try {
      setCompaniesLoading(true);
      const companiesResponse = await getCompanies('?limit=9999&type=vendor&ordering=name');
      setCompanies(companiesResponse || []);
    } catch (error) {
      console.error('Error fetching companies for display status verification:', error);
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  const fetchAndSetRows = useCallback(async () => {
    if (!selectedCompany?.id) {
      setRows([]);
      setRowCount(0);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const workflowResponse = await fetchWorkflows({
        service_provider: selectedCompany.id,
      });

      const workflows = workflowResponse || [];

      const workflowsWithSurvey: WorkflowDisplayStatusVerificationRow[] = await Promise.all(
        workflows
          .filter(wf => wf.site || wf.application)
          .map(async (workflow: Workflow) => {
            if (workflow.bpSurvey) {
              const survey = await fetchSurvey(workflow.bpSurvey);
              let assessmentSurvey: AssessmentSurvey | null = null;

              if (workflow.assessmentSurvey) {
                assessmentSurvey = await fetchAssessmentSurvey(workflow.assessmentSurvey);
              }

              return {
                workflow,
                bpSurvey: survey,
                assessmentSurvey,
                company: selectedCompany,
                id: workflow.id,
              };
            }

            return {
              workflow,
              bpSurvey: null,
              assessmentSurvey: null,
              company: selectedCompany,
              id: workflow.id,
            };
          }),
      );

      setRows(workflowsWithSurvey);
      setRowCount(workflowsWithSurvey.length || 0);
    } catch (error) {
      console.error('Error fetching workflows for display status verification:', error);
      setRows([]);
      setRowCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompany]);

  useEffect(() => {
    fetchCompaniesForDropdown();
  }, [fetchCompaniesForDropdown]);

  useEffect(() => {
    fetchAndSetRows();
  }, [fetchAndSetRows]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Workflow ID',
      width: 120,
      filterOperators: stringContainsOnly,
      valueGetter: (params) => params.row.workflow.id || '',
      renderCell: (params) => <span>{params.row.workflow.id || ''}</span>,
    },
    {
      field: 'site_or_app_name',
      headerName: 'Site/App ID',
      width: 280,
      filterOperators: stringContainsOnly,
      valueGetter: (params) => getSiteOrAppDisplayValue(params.row.workflow),
      renderCell: (params) => <span>{getSiteOrAppDisplayValue(params.row.workflow)}</span>,
    },
    {
      field: 'displayStatus',
      headerName: 'Display Status',
      width: 400,
      filterOperators: stringContainsOnly,
      valueGetter: (params) =>
        params.row.workflow.displayStatus ? params.row.workflow.displayStatus.status : '',
      renderCell: (params) =>
        params.row.workflow.displayStatus ? (
          <WorkflowStatusDisplay
            isWorkflowActive={params.row.workflow.isActive}
            statusObject={params.row.workflow.displayStatus}
            assessmentSurvey={params.row.assessmentSurvey}
            workflowDisplayType={params.row.workflow.workflowType}
          />
        ) : (
          <span>{''}</span>
        ),
    },
    {
      field: 'status',
      headerName: 'Current Status',
      width: 400,
      filterOperators: stringContainsOnly,
      valueGetter: (params) => params.row.workflow.status || '',
      renderCell: (params) => (
        <WorkflowStatus
          company={params.row.company}
          survey={params.row.bpSurvey}
          assessmentSurvey={params.row.assessmentSurvey}
          workflow={params.row.workflow}
        />
      ),
    },
    {
      field: 'link',
      headerName: 'Link',
      width: 100,
      renderCell: (params) => (
        <a href={`/assessment/${params.row.workflow.assessment}/questionnaire/${params.row.assessmentSurvey?.survey.id}/`}><LinkIcon /></a>
      ),
    },
  ];

  return (
    <Card sx={{ padding: '2em' }}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Autocomplete
          options={companies}
          loading={companiesLoading}
          value={selectedCompany}
          onChange={(_, newValue) => {
            setSelectedCompany(newValue);
            setPage(0);
            setRows([]);
            setRowCount(0);
          }}
          getOptionLabel={(option) => option?.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Company"
              placeholder="Choose a company"
            />
          )}
          sx={{ maxWidth: 450 }}
        />

        <Box sx={{ width: '100%' }}>
          <DataGridPro
            pagination
            rowHeight={70}
            autoHeight={false}
            style={{ height: 700, width: '100%' }}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            rowsPerPageOptions={[100]}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setPage(0);
              setSortModel(model);
            }}
            filterModel={filterModelDraft}
            onFilterModelChange={(model) => {
              setPage(0);
              setFilterModelDraft(model);

              const normalized = normalizeFilterModel(model);
              if (!sameModel(normalized, filterModelApplied)) {
                setFilterModelApplied(normalized);
              }
            }}
            loading={isLoading}
            components={{
              Toolbar: BaseToolbar,
              LoadingOverlay: () => (
                <GridOverlay>
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <CircularProgress />
                  </div>
                </GridOverlay>
              ),
              NoRowsOverlay: () => (
                <GridOverlay>
                  <Box
                    sx={{
                      mt: 2,
                      color: 'text.secondary',
                      fontSize: 14,
                    }}
                  >
                    {selectedCompany
                      ? 'No workflows found for the selected company.'
                      : 'Select a company to load workflows.'}
                  </Box>
                </GridOverlay>
              ),
            }}
          />
        </Box>
      </Box>
    </Card>
  );
}